import "@/styles/components/modal.css";
import delegate from "delegate-it";
import A11yDialog from "a11y-dialog";
import ajax from "@/scripts/common/ajax";
import { fireEvent } from "@/scripts/common/util";

let activeModal;
let initialized = false;

const getModalMountRoot = () => window.MateflexCourtBuilderEmbed?.getModalRoot?.() ?? document.body;
const getModalStateRoot = () => window.MateflexCourtBuilderEmbed?.getModalStateRoot?.() ?? document.documentElement;
const getActiveModalWrapper = () => getModalMountRoot().querySelector("[data-modal-wrapper]");

const generateTemplate = (id, allowOverlayClose = true, title = false, asFull = false, allowButtonClose = true) => {
  const html = `
    <div aria-hidden="true" class="flex overflow-auto fixed inset-0 outline-none z-100 overflow-scroll-touch aria-hidden:hidden" role="dialog" data-modal-wrapper>
      <div class="fixed inset-0 z-0 bg-black/80 backdrop-blur-xs" data-a11y-dialog-hide data-modal-overlay></div>

      <button type="button" class="flex absolute top-4 right-4 rounded-full transition-colors lg:top-6 lg:right-6 hover:bg-white modal-close size-12 z-1 bg-brand-green xl:size-16" data-a11y-dialog-hide aria-label="Close dialog">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32" aria-hidden="true" focusable="false" class="m-auto w-2/5 shrink-0">
          <path fill="#212020" fill-rule="evenodd" d="M.643 28.255a2.194 2.194 0 0 0 3.103 3.102l12.252-12.254 12.254 12.253a2.194 2.194 0 0 0 3.102-3.102L19.101 16 31.354 3.745A2.194 2.194 0 0 0 28.252.642L15.999 12.897 3.745.643A2.194 2.194 0 0 0 .642 3.746L12.897 16z" clip-rule="evenodd"/>
        </svg>
      </button>

      <div data-modal-container>
        <svg class="text-white site-loader" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
        </svg>
      </div>
    </div>
  `;

  const template = document.createRange().createContextualFragment(html);

  template.querySelector("[data-modal-wrapper]").id = id;

  if (title) {
    template.querySelector("[data-modal-wrapper]").setAttribute("aria-labeledby", "modal-title");
  }

  if (asFull) {
    template.querySelector("[data-modal-wrapper]").classList.add("as-full");
  }

  if (!allowOverlayClose) {
    template.querySelector("[data-modal-overlay]").removeAttribute("data-a11y-dialog-hide");
  }

  if (!allowButtonClose) {
    template.querySelector("button[data-a11y-dialog-hide]").remove();
  }

  return template;
};

const populateModal = async (opts = {}, modalEl) => {
  const type = opts.type;
  const title = opts.title ?? "";
  const url = opts.url;

  const defaultClose = modalEl.querySelector(".modal-close");

  if (defaultClose) {
    defaultClose.classList.add("invisible");
  }

  let modalContent = "";

  if (type === "video") {
    modalContent = `
      <div class="mx-auto w-full max-w-6xl" role="document" data-modal-content>
        <h1 id="modal-title" class="sr-only">Watch ${title}</h1>
        <div class="relative aspect-video" data-modal-body>
          <button type="button" class="flex relative -top-16 ml-auto rounded-full transition-colors lg:-top-8 lg:-right-8 hover:bg-white size-12 z-1 bg-brand-green" data-a11y-dialog-hide aria-label="Close dialog">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32" aria-hidden="true" focusable="false" class="w-[35%] m-auto shrink-0">
              <path fill="#212020" fill-rule="evenodd" d="M.643 28.255a2.194 2.194 0 0 0 3.103 3.102l12.252-12.254 12.254 12.253a2.194 2.194 0 0 0 3.102-3.102L19.101 16 31.354 3.745A2.194 2.194 0 0 0 28.252.642L15.999 12.897 3.745.643A2.194 2.194 0 0 0 .642 3.746L12.897 16z" clip-rule="evenodd"/>
            </svg>
          </button>
          <iframe title="Watch ${title}" style="border: none" src="${url}?autoplay=1&rel=0&enablejsapi=1" allowfullscreen class="absolute inset-0 w-full h-full"></iframe>
        </div>
      </div>

    `;
  }

  if (type === "content") {
    modalContent = await ajax.get(url).text();
  }

  const content = document.createRange().createContextualFragment(modalContent);

  if (defaultClose) {
    if (content.querySelector("[data-a11y-dialog-hide]")) {
      defaultClose.remove();
    } else {
      defaultClose.classList.remove("invisible");
    }
  }

  modalEl.querySelector("[data-modal-container]").replaceChildren(content);

  fireEvent("modal:populate", document.documentElement, { modalEl });
};

const openModal = (opts = {}) => {
  const type = opts.type;
  const title = opts.title ?? false;
  const asFull = opts.asFull ?? false;
  const allowButtonClose = opts.allowButtonClose ?? true;

  let allowOverlayClose = opts.allowOverlayClose ?? true;

  if (type === "video") {
    allowOverlayClose = false;
  }

  const id = `modal-${Math.floor(Math.random() * Date.now())}`;
  const template = generateTemplate(id, allowOverlayClose, title, asFull, allowButtonClose);

  getModalMountRoot().append(template);

  const modalEl = document.getElementById(id);
  const modal = new A11yDialog(modalEl);

  modal.on("show", () => {
    getModalStateRoot().classList.add("has-modal-active");

    activeModal = modal;
  });

  modal.on("hide", () => {
    getModalStateRoot().classList.remove("has-modal-active");
    getActiveModalWrapper()?.remove();

    activeModal = null;
  });

  modal.show();

  populateModal(
    {
      type: type,
      url: opts.url ?? false,
    },
    modalEl,
  );
};

const handleModal = (e) => {
  e.preventDefault();

  const triggerEl = e.target.closest("[data-modal]");

  openModal({
    type: triggerEl.dataset.modal,
    title: triggerEl.dataset.modalTitle,
    url: triggerEl.href,
    allowOverlayClose: triggerEl.dataset.modalDisableOverlayClose === undefined,
  });
};

const handleModalClose = () => {
  if (activeModal) {
    activeModal.hide();
  }
};

const handleModalReplace = async (e) => {
  e.preventDefault();

  const triggerEl = e.target.closest("[data-modal-replace]");
  const url = triggerEl.href;
  const currentModal = getActiveModalWrapper();
  const modalContent = await ajax.get(url).text();

  currentModal.querySelector("[data-modal-container]").innerHTML = modalContent;
};

const handleModalOpen = (e) => {
  openModal(e.detail);
};

export default {
  init: () => {
    if (initialized) {
      return;
    }

    initialized = true;

    delegate("[data-modal]", "click", handleModal);
    delegate("[data-modal-replace]", "click", handleModalReplace);
    delegate("html", "modal:open", handleModalOpen);
    delegate("html", "modal:close", handleModalClose);

    // If we have video modals, load the YouTube API
    if (document.querySelectorAll('[data-modal="video"]').length) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";

      const firstScriptTag = document.getElementsByTagName("script")[0];

      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  },
};
